import React, { createContext, useContext, useState, useCallback } from 'react';
import { 
  Backdrop, 
  CircularProgress, 
  Typography, 
  Box 
} from '@mui/material';

interface LoadingContextType {
  isLoading: boolean;
  message?: string;
  showLoading: (message?: string) => void;
  hideLoading: () => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
  children: React.ReactNode;
}

const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string | undefined>();

  const showLoading = useCallback((loadingMessage?: string) => {
    setMessage(loadingMessage);
    setIsLoading(true);
  }, []);

  const hideLoading = useCallback(() => {
    setIsLoading(false);
    setMessage(undefined);
  }, []);

  const contextValue: LoadingContextType = {
    isLoading,
    message: message || '',
    showLoading,
    hideLoading,
  };

  return (
    <LoadingContext.Provider value={contextValue}>
      {children}

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
        open={isLoading}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2
          }}>
          <CircularProgress 
            color="inherit" 
            size={48}
            thickness={4}
          />
          
          {message && (
            <Typography
              variant="body1"
              sx={{
                color: "inherit",
                textAlign: "center",
                maxWidth: '300px'
              }}>
              {message}
            </Typography>
          )}
        </Box>
      </Backdrop>
    </LoadingContext.Provider>
  );
};

// Custom hook to use loading context
export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  
  return context;
};

// Higher-order component for automatic loading states
export const withLoading = <P extends object>(
  Component: React.ComponentType<P>
) => {
  return (props: P) => {
    const { showLoading, hideLoading } = useLoading();
    
    // You can extend this to automatically handle loading states
    // based on component lifecycle or props
    
    return <Component {...props} showLoading={showLoading} hideLoading={hideLoading} />;
  };
};

export default LoadingProvider;